import { trackEvent } from '../services/analytics';
import { clearSessionCache } from '../utils/sessionCache';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../firebase';
import { getRedirectResult, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { completeUserTutorial, ensureUserProfile, subscribeToTutorialCompletion, syncLocalUser } from '../services/firebaseData';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [tutorialCompleted, setTutorialCompleted] = useState(null);
    const [loading, setLoading] = useState(true);
    const [redirectHandled, setRedirectHandled] = useState(false);
    const [profileError, setProfileError] = useState(false);
    const [profileRetry, setProfileRetry] = useState(0);

    useEffect(() => {
        let cancelled = false;
        async function handleRedirect() {
            try {
                const result = await getRedirectResult(auth);
                if (result?.user) {
                    const provider = result.providerId === 'apple.com' ? 'apple' : 'google';
                    trackEvent('login', { method: provider });
                    const profile = await ensureUserProfile(result.user);
                    syncLocalUser(profile);
                    setCurrentUser(result.user);
                }
            } catch (error) {
                console.error('Firebase redirect sign-in failed:', error);
            } finally {
                if (!cancelled) {
                    setRedirectHandled(true);
                }
            }
        }
        handleRedirect();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!redirectHandled) {
            return;
        }

        let sessionVersion = 0;
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            const version = ++sessionVersion;
            clearSessionCache();
            setProfileError(false);
            setTutorialCompleted(null);
            setUserProfile(null);
            try {
                setCurrentUser(user);
                if (user) {
                    try {
                        const profile = await ensureUserProfile(user);
                        if (version !== sessionVersion) return;
                        syncLocalUser(profile);
                        setUserProfile(profile);
                        setTutorialCompleted(Boolean(profile.tutorialCompletedAt));
                    } catch (error) {
                        if (version !== sessionVersion) return;
                        console.error('Firebase profile sync failed:', error);
                        setProfileError(true);
                    }
                } else {
                    localStorage.removeItem('user_id');
                    localStorage.removeItem('user_complete_name');
                    localStorage.removeItem('user_avatar_url');
                    localStorage.removeItem('profile_completed');
                }
            } finally {
                if (version === sessionVersion) setLoading(false);
            }
        });

        return () => { sessionVersion += 1; unsubscribe(); };
    }, [redirectHandled, profileRetry]);

    // Other open devices lose access as soon as deletion marks the profile.
    useEffect(() => {
        if (!currentUser || !userProfile) return;
        const endSession = () => {
            localStorage.clear();
            signOut(auth).catch(console.error);
        };
        return onSnapshot(doc(db, 'users', currentUser.uid), (snapshot) => {
            if (snapshot.data()?.deletedAt) endSession();
        }, (error) => {
            if (error.code === 'permission-denied') endSession();
        });
    }, [currentUser, userProfile]);

    const tutorialReady = tutorialCompleted !== null;
    useEffect(() => {
        if (loading || !currentUser || !tutorialReady) return;
        let active = true;
        const unsubscribe = subscribeToTutorialCompletion(currentUser.uid, (completed) => {
            if (active) setTutorialCompleted((current) => Boolean(current || completed));
        });
        return () => { active = false; unsubscribe(); };
    }, [currentUser, loading, tutorialReady]);

    const completeTutorial = async () => {
        const userId = currentUser?.uid;
        await completeUserTutorial(userId);
        if (auth.currentUser?.uid === userId) setTutorialCompleted(true);
    };

    const value = {
        currentUser,
        userProfile,
        setUserProfile,
        tutorialCompleted,
        completeTutorial,
        userId: currentUser ? currentUser.uid : null,
    };

    return (
        <AuthContext.Provider value={value}>
            {loading ? (
                <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', textAlign: 'center' }} role="status">
                    <Box>
                        <CircularProgress aria-label="Avvio di Convivial" />
                        <Typography sx={{ mt: 2 }}>Avvio di Convivial…</Typography>
                    </Box>
                </Box>
            ) : profileError ? (
                <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', p: 3, textAlign: 'center' }}>
                    <Box role="alert">
                        <Typography>Non è stato possibile caricare il tuo profilo. Riprova per continuare.</Typography>
                        <Button onClick={() => {
                            setLoading(true);
                            setProfileRetry((attempt) => attempt + 1);
                        }}>Riprova</Button>
                    </Box>
                </Box>
            ) : children}
        </AuthContext.Provider>
    );
}
